// src/lib/engagements.ts
//
// Acceso delegado y auditado entre orgs (ADR-007 · Contrato F1 · mcp_django apps/engagements).
// Una consultora entra a las empresas cliente por un `Engagement` explícito; el JWT
// sigue siendo el de la consultora. Estas llamadas son SIEMPRE de «mi org» (nunca
// llevan X-Acting-Org): aquí se administra justamente la delegación.
//
//   GET   /api/v1/engagements                  → {org_id, engagements:[…]} (rol consultora|cliente)
//   POST  /api/v1/engagements/empresas         → {engagement, empresa:{org_id,nombre,estado,checklist}}
//   PATCH /api/v1/engagements/{id}             → solo la empresa CLIENTE u operador (consultora → 403)
//   GET   /api/v1/engagements/{id}/bitacora    → {engagement, eventos:[…]}
//   POST  /api/v1/engagements/promover-cliente → dry_run: {cliente, empresa_propuesta, se_moveria}
//                                                aplicar:  {movido, engagement, empresa}
//
// Normalización en el borde: la UI solo recibe la forma limpia.

import { decodeJwt } from '@/lib/api_superadmin';

const DJANGO_PROXY = '/django-api';
const BASE = '/api/v1/engagements';

export type EngagementModo = 'consultoria' | 'con_colaboradores';
export type EngagementEstado = 'activo' | 'pausado' | 'revocado';
/** Papel de MI org en el engagement. */
export type EngagementRol = 'consultora' | 'cliente' | 'operador';

export interface Engagement {
  id: string;
  rol: EngagementRol;
  consultora_org: string;
  consultora_nombre: string;
  cliente_org: string;
  cliente_nombre: string;
  modo: EngagementModo;
  scopes: string[];
  estado: EngagementEstado;
  vigente_hasta: string | null;
  /** Calculado por el servidor: activo y no vencido. */
  vigente: boolean;
  otorgado_por: string;
  created_at: string | null;
  updated_at: string | null;
}

export const MODO_LABEL: Record<EngagementModo, string> = {
  consultoria: 'Consultoría',
  con_colaboradores: 'Con colaboradores',
};
export const MODO_HINT: Record<EngagementModo, string> = {
  consultoria: 'Solo tu consultora y RR. HH. de la empresa entran al sistema; los colaboradores participan por enlace.',
  con_colaboradores: 'Cada colaborador tiene cuenta, «Mi puesto» y su agente. Se puede activar después sin perder datos.',
};
export const ESTADO_LABEL: Record<EngagementEstado, string> = {
  activo: 'Activo',
  pausado: 'Pausado',
  revocado: 'Revocado',
};
/** Permisos que la UI ofrece (nunca `diagnostics:sensitive`: lo otorga solo la empresa/operador). */
export const SCOPE_LABEL: Record<string, string> = {
  'diagnostics:read': 'Ver',
  'diagnostics:create': 'Crear',
  'diagnostics:update': 'Editar',
  'diagnostics:delete': 'Borrar',
  'diagnostics:sensitive': 'Datos sensibles',
};

export class EngagementError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : v == null ? '' : String(v));

export function normEngagement(r: any): Engagement | null {
  if (!r || typeof r !== 'object') return null;
  const id = str(r.id);
  const cliente_org = str(r.empresa?.org_id ?? r.cliente_org);
  const consultora_org = str(r.consultora?.org_id ?? r.consultora_org);
  if (!id || !cliente_org) return null;
  const modo: EngagementModo = str(r.modo) === 'con_colaboradores' ? 'con_colaboradores' : 'consultoria';
  const e = str(r.estado);
  const estado: EngagementEstado = e === 'activo' || e === 'pausado' ? e : 'revocado';
  const rolRaw = str(r.rol);
  // El backend dice `cliente`; se acepta `empresa` como alias.
  const rol: EngagementRol = rolRaw === 'consultora' ? 'consultora' : rolRaw === 'cliente' || rolRaw === 'empresa' ? 'cliente' : 'operador';
  const vigente_hasta = str(r.vigente_hasta) || null;
  return {
    id,
    rol,
    consultora_org,
    consultora_nombre: str(r.consultora?.nombre ?? r.consultora_nombre) || consultora_org,
    cliente_org,
    cliente_nombre: str(r.empresa?.nombre ?? r.cliente_nombre) || cliente_org,
    modo,
    scopes: Array.isArray(r.scopes) ? r.scopes.map(str).filter(Boolean) : [],
    estado,
    vigente_hasta,
    // Si el servidor no lo manda, se calcula igual que él (activo y sin vencer).
    vigente: typeof r.vigente === 'boolean' ? r.vigente : estado === 'activo' && !(vigente_hasta && Date.parse(vigente_hasta) <= Date.now()),
    otorgado_por: str(r.otorgado_por),
    created_at: str(r.created_at) || null,
    updated_at: str(r.updated_at) || null,
  };
}

/** ¿Se puede trabajar HOY en esta empresa con `scope`? Solo si MI org es la consultora. */
export function engagementUsable(e: Engagement, scope?: string): boolean {
  if (e.rol !== 'consultora') return false;
  if (e.estado !== 'activo' || !e.vigente) return false;
  if (scope && !e.scopes.includes(scope)) return false;
  return true;
}

/** Motivo legible por el que un engagement no sirve hoy. */
export function engagementBlocker(e: Engagement, scope?: string): string {
  if (e.estado === 'revocado') return 'el acceso fue revocado';
  if (e.estado === 'pausado') return 'el acceso está en pausa';
  if (!e.vigente) return 'el acceso ya venció';
  if (scope && !e.scopes.includes(scope)) return 'la empresa no otorgó este permiso';
  return '';
}

function headers(json = false): Record<string, string> {
  const token = localStorage.getItem('auth_token');
  return { Authorization: `Bearer ${token ?? ''}`, ...(json ? { 'Content-Type': 'application/json' } : {}) };
}

/** Texto del error de Django/Ninja (`detail` string o lista de validación). */
export async function errorDetail(res: Response): Promise<string> {
  try {
    const b = await res.json();
    if (typeof b?.detail === 'string') return b.detail;
    if (Array.isArray(b?.detail)) return b.detail.map((d: any) => str(d?.msg)).filter(Boolean).join(' · ') || `Error ${res.status}`;
    if (typeof b?.message === 'string') return b.message;
  } catch {
    /* cuerpo vacío */
  }
  return `Error ${res.status}`;
}

async function asJson<T>(res: Response): Promise<T> {
  if (!res.ok) throw new EngagementError(res.status, await errorDetail(res));
  return res.json() as Promise<T>;
}

/** org_id del JWT (mi org). Vacío = sin sesión. */
export function principalOrg(): string {
  const t = localStorage.getItem('auth_token');
  const p = t ? decodeJwt(t) : null;
  return p && typeof p.org_id === 'string' ? p.org_id : '';
}

/** Todos mis engagements: donde soy consultora (mis empresas) y donde soy la empresa (quién me atiende). */
export async function listEngagements(signal?: AbortSignal): Promise<Engagement[]> {
  const raw = await asJson<any>(await fetch(`${DJANGO_PROXY}${BASE}`, { headers: headers(), signal }));
  const list = Array.isArray(raw?.engagements) ? raw.engagements : Array.isArray(raw) ? raw : [];
  return list.map(normEngagement).filter((e: Engagement | null): e is Engagement => !!e);
}

export interface NuevaEmpresa {
  nombre: string;
  modo: EngagementModo;
  contacto_nombre: string;
  contacto_email: string;
}

export interface ChecklistPaso {
  paso: string;
  ok: boolean | null;
  detalle: string;
}

export interface EmpresaCreada {
  engagement: Engagement | null;
  org_id: string;
  nombre: string;
  /** Estado del alta (org + módulo + llaves + correo + config). */
  estado: string;
  checklist: ChecklistPaso[];
}

function normEmpresa(raw: any): EmpresaCreada {
  const emp = raw?.empresa ?? {};
  return {
    engagement: normEngagement(raw?.engagement),
    org_id: str(emp.org_id),
    nombre: str(emp.nombre),
    estado: str(emp.estado),
    checklist: (Array.isArray(emp.checklist) ? emp.checklist : []).map((p: any) => ({
      paso: str(p?.paso),
      ok: typeof p?.ok === 'boolean' ? p.ok : null,
      detalle: str(p?.detalle),
    })),
  };
}

export async function crearEmpresa(body: NuevaEmpresa): Promise<EmpresaCreada> {
  const raw = await asJson<any>(
    await fetch(`${DJANGO_PROXY}${BASE}/empresas`, { method: 'POST', headers: headers(true), body: JSON.stringify(body) }),
  );
  return normEmpresa(raw);
}

/** Solo la empresa cliente (rol `cliente`) u operador; la consultora recibe 403. */
export async function actualizarEngagement(
  id: string,
  body: Partial<{ modo: EngagementModo; estado: EngagementEstado; scopes: string[]; vigente_hasta: string | null }>,
): Promise<Engagement | null> {
  const raw = await asJson<any>(
    await fetch(`${DJANGO_PROXY}${BASE}/${encodeURIComponent(id)}`, { method: 'PATCH', headers: headers(true), body: JSON.stringify(body) }),
  );
  return normEngagement(raw);
}

export interface BitacoraEvento {
  id: string;
  at: string;
  actor: string;
  accion: string;
  endpoint: string;
  scope: string;
  detalle: Record<string, unknown>;
}

export async function bitacora(id: string): Promise<BitacoraEvento[]> {
  const raw = await asJson<any>(await fetch(`${DJANGO_PROXY}${BASE}/${encodeURIComponent(id)}/bitacora`, { headers: headers() }));
  return (Array.isArray(raw?.eventos) ? raw.eventos : []).map((e: any) => ({
    id: str(e?.id),
    at: str(e?.at),
    actor: str(e?.actor),
    accion: str(e?.accion),
    endpoint: str(e?.endpoint),
    scope: str(e?.scope),
    detalle: e?.detalle && typeof e.detalle === 'object' ? e.detalle : {},
  }));
}

/** Conteos de lo que se mueve al promover (proyectos, participantes, análisis…). */
export type Conteos = Record<string, number>;

export interface PromocionResultado {
  dry_run: boolean;
  cliente_nombre: string;
  empresa_org: string;
  empresa_nombre: string;
  conteos: Conteos;
  /** Solo al aplicar. */
  empresa: EmpresaCreada | null;
}

export const CONTEO_LABEL: Record<string, string> = {
  proyectos: 'Proyectos',
  participantes: 'Participantes',
  analisis: 'Análisis',
  reportes: 'Reportes',
  organigramas: 'Organigramas',
  respuestas_instrumento: 'Respuestas de cuestionario',
};

export function normPromocion(raw: any): PromocionResultado {
  const r = raw && typeof raw === 'object' ? raw : {};
  const dry = r.dry_run !== false;
  const src = (dry ? r.se_moveria : r.movido) ?? {};
  const conteos: Conteos = {};
  Object.entries(src).forEach(([k, v]) => {
    const n = Number(v);
    if (Number.isFinite(n)) conteos[k] = n;
  });
  const empresa = dry ? null : normEmpresa(r);
  return {
    dry_run: dry,
    cliente_nombre: str(r.cliente?.nombre),
    empresa_org: str(r.empresa_propuesta?.org_id ?? r.empresa?.org_id),
    empresa_nombre: str(r.empresa_propuesta?.nombre ?? r.empresa?.nombre),
    conteos,
    empresa,
  };
}

export async function promoverCliente(body: {
  client_id: string;
  modo: EngagementModo;
  dry_run: boolean;
  nombre?: string;
}): Promise<PromocionResultado> {
  const raw = await asJson<any>(
    await fetch(`${DJANGO_PROXY}${BASE}/promover-cliente`, { method: 'POST', headers: headers(true), body: JSON.stringify(body) }),
  );
  return normPromocion(raw);
}

/** Clientes (DiagClient) de MI org — para «Promover cliente existente». Sin X-Acting-Org. */
export async function listMyDiagClients(): Promise<{ id: string; name: string; sector: string }[]> {
  const raw = await asJson<any>(await fetch(`${DJANGO_PROXY}/api/v1/diagnostics/clients`, { headers: headers() }));
  const list = Array.isArray(raw?.clients) ? raw.clients : Array.isArray(raw) ? raw : [];
  return list
    .map((c: any) => ({ id: str(c?.id), name: str(c?.name ?? c?.nombre), sector: str(c?.sector) }))
    .filter((c: { id: string; name: string }) => c.id && c.name);
}

/** Avisa a los selectores «Empresa» montados que la lista cambió (alta/promoción). */
export const ENGAGEMENTS_CHANGED = 'orion:engagements-changed';
export function notifyEngagementsChanged(): void {
  window.dispatchEvent(new Event(ENGAGEMENTS_CHANGED));
}
